import { useState } from 'react';
import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';

interface VideoTestimonialsData {
  title: string;
  highlight: string;
  videos: { thumbnail: string; title: string; duration: string; author: string }[];
}

export default function VideoTestimonials() {
  const [playing, setPlaying] = useState<number | null>(null);
  const { t } = useTranslation();
  const { data, loading, error, refresh } = useSiteContent<VideoTestimonialsData>(
    'why_us_video_testimonials',
  );

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-primary-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {data.title}{' '}
            <span className="text-primary-700">{data.highlight}</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-12">
            {data.videos.map((video, idx) => (
              <div
                key={idx}
                className="group relative rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setPlaying(playing === idx ? null : idx)}
              >
                <div className="relative aspect-video">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />

                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <i className="ri-play-fill text-primary-500 text-2xl md:text-3xl ml-1" />
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded">
                    {video.duration}
                  </div>
                </div>

                <div className="p-4 bg-background-50 border border-background-200/70 border-t-0 rounded-b-xl">
                  <p className="text-sm font-semibold text-foreground-950 mb-1">{video.title}</p>
                  <p className="text-xs text-foreground-500">{video.author}</p>
                </div>

                {/* Fake video overlay */}
                {playing === idx && (
                  <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10">
                    <div className="text-center text-white">
                      <i className="ri-movie-line text-4xl mb-2" />
                      <p className="text-sm font-medium">{t('whyus.videoTestimonial')}</p>
                      <p className="text-xs text-white/70 mt-1">{t('whyus.clickToClose')}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}